import {
  getInRepairRagDisplayLabel,
  normalizeInRepairPhase,
  IN_REPAIR_EDD_TEXT_STYLES,
} from "../data/inRepairListingConstants";

const matchesSearch = (row, search) => {
  if (!search?.trim()) return true;
  const term = search.trim().toLowerCase();
  const fields = [
    row.ro_number,
    row.part_number,
    row.serial_number,
    row.description,
    row.vendor,
    row.rve_owner,
  ];
  return fields.some((field) => field?.toLowerCase().includes(term));
};

const matchesRagPill = (row, ragFilter) => {
  if (!ragFilter || ragFilter === "total") return true;
  if (ragFilter === "no_edd") {
    return (
      !row.current_edd ||
      row.rag_status === "No EDD" ||
      String(row.rag_status || "").toLowerCase().includes("no edd")
    );
  }
  if (ragFilter === "stalled") return row.rag_status === "Stalled";
  if (ragFilter === "green") return row.rag_status === "As per plan";
  if (ragFilter === "amber") return row.rag_status === "Nearing Due";
  if (ragFilter === "red") {
    return row.rag_status === "Overdue" || row.rag_status === "Over due";
  }
  return row.rag_status === ragFilter;
};

export const matchesGroupSubrowFilter = (row, filterKey) => {
  if (!filterKey) return true;
  if (filterKey === "p1") return row.priority_tier === "P1";
  return matchesRagPill(row, filterKey);
};

const getFilterValue = (field) => {
  if (!field) return null;
  if (typeof field === "object") return field.value ?? field.label ?? null;
  if (field === "All") return null;
  return field;
};

const getArrayFilterValues = (field) => {
  if (!field || !Array.isArray(field) || field.length === 0) return null;
  return field.map((item) =>
    typeof item === "object" ? item.value ?? item.label : item
  );
};

export const filterInRepairListingRows = (rows, filters) => {
  return rows.filter((row) => {
    const rve = getFilterValue(filters.rve);
    const vendors = getArrayFilterValues(filters.repair_vendors);
    const phase = getFilterValue(filters.phase);
    const priority = getFilterValue(filters.priority_tier);
    const workstream = getFilterValue(filters.workstream);
    const scGroup = getFilterValue(filters.sc_group);

    if (rve && row.rve_owner !== rve) return false;
    if (vendors && !vendors.includes(row.vendor)) return false;
    if (phase && normalizeInRepairPhase(row.phase) !== phase) return false;
    if (priority && row.priority_tier !== priority) return false;
    if (workstream && row.commercial_workstream !== workstream) return false;
    if (scGroup && row.sc_group !== scGroup) return false;
    if (!matchesRagPill(row, filters.ragFilter)) return false;
    if (!matchesSearch(row, filters.search)) return false;
    return true;
  });
};

export const groupInRepairListingRows = (rows, groupBy) => {
  const groupField =
    typeof groupBy === "object" ? groupBy?.value : groupBy || "sc_group";

  if (groupField === "none") {
    return [
      {
        key: "flat",
        label: "",
        count: rows.length,
        summary: {},
        items: rows,
        hideHeader: true,
      },
    ];
  }

  const groups = rows.reduce((acc, row) => {
    const key = row[groupField] || "Unknown";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(row);
    return acc;
  }, {});

  return Object.entries(groups)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => {
      const summary = items.reduce(
        (stats, item) => {
          stats.red += item.rag_status === "red" ? 1 : 0;
          stats.amber += item.rag_status === "amber" ? 1 : 0;
          stats.stalled += item.rag_status === "Stalled" ? 1 : 0;
          stats.no_edd += !item.current_edd ? 1 : 0;
          stats.p1 += item.priority_tier === "P1" ? 1 : 0;
          return stats;
        },
        { red: 0, amber: 0, stalled: 0, no_edd: 0, p1: 0 }
      );

      return {
        key,
        label: key,
        count: items.length,
        summary,
        items,
      };
    });
};

export const sortInRepairListingRows = (rows, sortBy, sortOrder) => {
  if (!sortBy) return rows;

  const sorted = [...rows].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === "number" && typeof bVal === "number") {
      return aVal - bVal;
    }

    return String(aVal).localeCompare(String(bVal));
  });

  return sortOrder === "desc" ? sorted.reverse() : sorted;
};

export const paginateRows = (rows, page, perPage) => {
  const start = (page - 1) * perPage;
  return rows.slice(start, start + perPage);
};

export const buildInRepairTableTreeData = (rows, groupBy) => {
  const groupField =
    typeof groupBy === "object" ? groupBy?.value : groupBy || "sc_group";

  if (groupField === "none") {
    return rows;
  }

  return groupInRepairListingRows(rows, groupBy).map((group) => ({
    id: `group-${group.key}`,
    _isGroupRow: true,
    groupKey: group.key,
    groupLabel: group.label,
    groupField,
    groupCount: group.count,
    subRowCount: group.count,
    summary: group.summary,
    p1Count: group.summary?.p1 ?? 0,
    subRows: group.items,
  }));
};

export const getInRepairListingColumnPreferences = (columnData = []) =>
  columnData
    .filter((column) => !["expand", "row_icon", "select", "action"].includes(column.id))
    .map((column, index) => ({
      key_name: column.id,
      display_name:
        typeof column.header === "string"
          ? column.header
          : typeof column.columnDef?.header === "string"
            ? column.columnDef.header
            : column.id,
      active:
        column.isVisible !== false && column.columnDef?.isVisible !== false,
      order: index + 1,
    }));

export const mapRepairOrderRagListingRow = (row, index) => {
  const ragStatus = getInRepairRagDisplayLabel(
    row.rag_status ?? row.rag_label ?? row.status_label
  );

  return {
    id: row.id ?? row.ro_id ?? `${row.ro_number ?? "ro"}-${index}`,
    sc_group: row.sc_group,
    ro_number: row.ro_number,
    open_ro_count: row.open_ro_count,
    part_number: row.part_number,
    serial_number: row.serial_number,
    description: row.description,
    vendor: row.vendor,
    rve_owner: row.rve_owner ?? row.rve,
    open_date: row.open_date,
    current_edd: row.current_edd,
    age_days: row.age_days,
    phase: normalizeInRepairPhase(row.phase ?? row.stage),
    priority_tier: row.priority_tier,
    commercial_workstream: row.commercial_workstream ?? row.workstream,
    rag_status: ragStatus,
    fmv: row.fmv,
    suggested_action: row.suggested_action,
    row_icon: row.row_icon ?? getRepairOrderRagRowIcon(ragStatus),
  };
};

export const resolveInRepairRowStatusIcon = (ragStatus) => {
  const value = String(ragStatus || "").toLowerCase();

  if (value.includes("stalled") || value.includes("purple")) {
    return "purple";
  }

  if (
    value.includes("no edd") ||
    value.includes("gray") ||
    value.includes("grey")
  ) {
    return "grey";
  }

  if (
    value.includes("overdue") ||
    value.includes("over due") ||
    value.includes("red")
  ) {
    return "red";
  }

  if (value.includes("nearing") || value.includes("amber")) {
    return "amber";
  }

  if (value.includes("as per plan") || value.includes("green")) {
    return "green";
  }

  return "grey";
};

export const getInRepairEddTextStyle = (ragStatus) => {
  const statusType = resolveInRepairRowStatusIcon(ragStatus);
  return IN_REPAIR_EDD_TEXT_STYLES[statusType] ?? {};
};

const getRepairOrderRagRowIcon = (ragStatus) =>
  resolveInRepairRowStatusIcon(ragStatus);

const mapGroupSummary = (group = {}) => {
  const summary = group.summary ?? group;

  return {
    green: summary.Green ?? summary.green ?? 0,
    amber: summary.Amber ?? summary.amber ?? 0,
    red: summary.Red ?? summary.red ?? summary.red_count ?? 0,
    no_edd: summary.Gray ?? summary.no_edd ?? summary.gray_count ?? 0,
    stalled:
      summary.Purple ??
      summary.stalled ??
      summary.stalled_count ??
      0,
  };
};

const isApiGroupRowWithItems = (item) =>
  item &&
  Array.isArray(item.items) &&
  (item.group_key != null || item.group_label != null);

const isApiGroupSummaryRow = (item) =>
  item &&
  (item.group_key != null || item.group_label != null) &&
  !Array.isArray(item.items);

const getGroupSubRowCount = (group = {}) => {
  if (typeof group.subRows === "number") {
    return group.subRows;
  }

  if (Array.isArray(group.subRows)) {
    return group.subRows.length;
  }

  if (Array.isArray(group.items)) {
    return group.items.length;
  }

  return group.ro_count ?? 0;
};

const mapApiGroupRow = (group, idx, groupField) => {
  const childRows = group.items ?? [];
  const subRowCount = getGroupSubRowCount(group);

  return {
    id: `group-${group.group_key ?? group.group_label ?? idx}`,
    _isGroupRow: true,
    groupKey: group.group_key ?? group.group_label,
    groupLabel: group.group_label ?? group.group_key,
    sc_group: group.sc_group,
    groupField,
    groupCount: group.ro_count ?? subRowCount,
    subRowCount,
    summary: mapGroupSummary(group),
    p1Count: group.p1_count ?? 0,
    subRows: childRows.map((row, index) => mapRepairOrderRagListingRow(row, index)),
    _subRowsLoaded: childRows.length > 0,
  };
};

const isFlatListingRow = (item) => item?.ro_number != null;

export const extractRepairOrderRagSubrowItems = (payload) => {
  const responseData = payload?.data;

  if (!responseData) {
    return [];
  }

  if (Array.isArray(responseData?.subRows)) {
    return responseData.subRows;
  }

  if (Array.isArray(responseData?.[0]?.subRows)) {
    return responseData[0].subRows;
  }

  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }

  if (Array.isArray(responseData?.[0]?.items)) {
    return responseData[0].items;
  }

  if (Array.isArray(responseData) && responseData[0]?.ro_number) {
    return responseData;
  }

  return [];
};

export const mergeRepairOrderRagGroupSubrows = (
  tableData = [],
  repairOrderRagSubrows = {},
  repairOrderRagSubrowLoading = {}
) =>
  tableData.map((row) => {
    if (!row?._isGroupRow) {
      return row;
    }

    const hasCachedSubrows = Object.prototype.hasOwnProperty.call(
      repairOrderRagSubrows,
      row.id
    );
    const cachedRows = repairOrderRagSubrows[row.id];
    const isLoading = repairOrderRagSubrowLoading[row.id];

    if (hasCachedSubrows) {
      return {
        ...row,
        subRows: (cachedRows ?? []).map((item, index) =>
          mapRepairOrderRagListingRow(item, index)
        ),
        _subRowsLoaded: true,
        _subRowsLoading: false,
      };
    }

    if (isLoading) {
      return {
        ...row,
        subRows: [],
        _subRowsLoading: true,
      };
    }

    return {
      ...row,
      subRows: row._subRowsLoaded ? row.subRows : [],
    };
  });

export const mapRepairOrderRagListingTableData = (payload, groupBy) => {
  if (!payload) return [];

  const list = Array.isArray(payload.data) ? payload.data : [];

  if (!list.length) {
    return [];
  }

  const groupField =
    typeof groupBy === "object" ? groupBy?.value : groupBy || "sc_group";

  if (groupField === "none") {
    if (isApiGroupRowWithItems(list[0])) {
      return list.flatMap((group) =>
        (group.items ?? []).map((row, index) =>
          mapRepairOrderRagListingRow(row, index)
        )
      );
    }

    return list
      .filter(isFlatListingRow)
      .map((row, index) => mapRepairOrderRagListingRow(row, index));
  }

  if (isApiGroupSummaryRow(list[0]) || isApiGroupRowWithItems(list[0])) {
    return list.map((group, idx) => {
      const mappedGroup = mapApiGroupRow(group, idx, groupField);

      if (isApiGroupSummaryRow(group)) {
        return {
          ...mappedGroup,
          subRows: [],
          _subRowsLoaded: false,
        };
      }

      return mappedGroup;
    });
  }

  const rows = list
    .filter(isFlatListingRow)
    .map((row, index) => mapRepairOrderRagListingRow(row, index));

  return buildInRepairTableTreeData(rows, groupBy);
};

export const mapRepairOrderRagSummary = (payload) => {
  const summary = payload?.data ?? {};

  return {
    total: summary.total ?? 0,
    green: summary.Green ?? summary.green ?? 0,
    amber: summary.Amber ?? summary.amber ?? 0,
    red: summary.Red ?? summary.red ?? 0,
    no_edd: summary.Gray ?? summary.no_edd ?? 0,
    stalled: summary.Purple ?? summary.stalled ?? 0,
  };
};

export const mapRepairOrderRagDetail = (payload) => {
  const data = payload?.data;
  if (!data) return null;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row !== "object") return null;

  return mapRepairOrderRagListingRow(row, 0);
};
